import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Plus, Package, Settings } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { CREATE_ITEM } from '../graphql/mutations';
import imageCompression from 'browser-image-compression';
import DigitalLinkForm from './DigitalLinkForm';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useDigitalAssetCleanup } from '../hooks/useDigitalAssetCleanup';


const AddItemPopup = ({ isOpen, onClose, onSubmit, categories, quickAddCategory, catalogId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [image, setImage] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [repeatType, setRepeatType] = useState('');
  const [repeatTime, setRepeatTime] = useState('');
  const [repeatDay, setRepeatDay] = useState('');
  const [repeatDate, setRepeatDate] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formattedPrice, setFormattedPrice] = useState('');

  // Digital product settings state
  const [digitalSettings, setDigitalSettings] = useState({
    downloadLimit: -1,
    linkExpiryHours: -1,
    allowPreview: false,
    previewDuration: 30
  });

  // Inventory management states
  const [manageInventory, setManageInventory] = useState(false);
  const [initialStock, setInitialStock] = useState(0);
  const [sku, setSku] = useState('');
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [showInventory, setShowInventory] = useState(false);

  // State for product type and digital upload flow
  const [productType, setProductType] = useState('PHYSICAL');
  const [showDigitalUpload, setShowDigitalUpload] = useState(false);
  const [step, setStep] = useState('CREATE_ITEM'); // 'CREATE_ITEM' or 'UPLOAD_ASSETS'
  const [createdItem, setCreatedItem] = useState(null);

  // State for pending digital assets (before item creation)
  const [pendingDigitalAssets, setPendingDigitalAssets] = useState([]);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [pendingDigitalLinks, setPendingDigitalLinks] = useState([]);
  const [digitalAssetTab, setDigitalAssetTab] = useState('files');
  const [digitalAssetType, setDigitalAssetType] = useState('files'); // 'files' or 'links'

  const { trackUpload, cleanupTempFiles, clearTracking } = useDigitalAssetCleanup();


  const {
    checkDigitalFileSize,
    checkDigitalFileCount,
    checkTotalDigitalSize,
    limits
  } = usePlanLimits(catalogId);

  useEffect(() => {
    if (isOpen && quickAddCategory) {
      setCategory(quickAddCategory.id);
    }
  }, [isOpen, quickAddCategory]);

  const formatPrice = (value) => {
    // Remove any non-digit characters
    const numericValue = value.replace(/[^\d]/g, '');
    // Format with commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handlePriceChange = (e) => {
    const rawValue = e.target.value;
    const formattedValue = formatPrice(rawValue);
    setFormattedPrice(formattedValue);
    // Store the numeric value without commas
    setPrice(rawValue.replace(/,/g, ''));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      // Create price variant with inventory data if inventory management is enabled
      const priceVariant = {
        variant: 'default',
        price: parseFloat(price)
      };

      // Add inventory fields if inventory management is enabled
      if (manageInventory) {
        priceVariant.stock = initialStock;
        priceVariant.sku = sku;
        priceVariant.lowStockThreshold = lowStockThreshold;
      }

      const formData = {
        name,
        description,
        priceVariants: [priceVariant],
        catalog: catalogId,
        category: category === '' ? null : category,
        image,
        scheduledStart,
        scheduledEnd,
        repeatAvailability: repeatType ? [{
          type: repeatType,
          time: repeatTime,
          day: repeatDay,
          date: repeatDate
        }] : null,
        isVisible: true,
        manageInventory,
        stockTotal: manageInventory ? initialStock : 0,
        productType,
        requiresShipping: productType !== 'DIGITAL',
        // Include digital settings for digital/hybrid products
        ...(showDigitalUpload && {
          digitalSettings: digitalSettings
        }),

        pendingDigitalAssets: pendingDigitalAssets.length > 0 ?
          pendingDigitalAssets.map(asset => ({
            filename: asset.filename,
            originalName: asset.originalName,
            fileSize: asset.fileSize,
            fileType: asset.fileType,
            isTemp: asset.isTemp,
            uploadMethod: asset.uploadMethod,
            assetType: 'FILE',
            ...(asset.uploadMethod === 'base64' && { base64Data: asset.base64Data }),
            ...(asset.uploadMethod === 'direct' && { storageUrl: asset.storageUrl })
          })) : [],
        digitalLinks: pendingDigitalLinks.length > 0 ?
          pendingDigitalLinks.map(link => ({
            linkUrl: link.linkUrl,
            linkType: link.linkType,
            linkDescription: link.linkDescription,
            originalName: link.originalName || `${link.linkType.toLowerCase()}_link`
          })) : []
      };

      const result = await onSubmit(formData);

      // Clear tracking since files are now saved
      clearTracking();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating item:', error);
      // Keep tracking for cleanup on cancel
      onSubmit(null, error);
    } finally {
      setIsAdding(false);
    }
  };


  const handleCancel = async () => {
    // Cleanup any uploaded temporary files
    const keysToCleanup = pendingDigitalAssets
      .filter(a => a.uploadMethod === 'direct' && a.filename)
      .map(a => a.filename);

    if (keysToCleanup.length > 0) {
      await cleanupTempFiles(keysToCleanup);
    }

    // Clear tracking after cleanup
    clearTracking();
    resetForm();
    onClose();
  };

  const handleDigitalSettingsChange = (setting, value) => {
    setDigitalSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };


  // HandleDigitalAssetAdd to use smaller chunks or direct upload

  // Alternative upload method for large files
  const uploadLargeFile = async (file, onProgress) => {
    try {
      const presignResponse = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/upload/presigned`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({
            filename: file.name,
            fileType: file.type,
            fileSize: file.size
          })
        }
      );

      if (!presignResponse.ok) {
        const error = await presignResponse.json();
        throw new Error(error.error || 'Failed to get upload URL');
      }

      const { uploadUrl, publicUrl, key } = await presignResponse.json();

      // Track the upload for potential cleanup
      trackUpload(key);

      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const percentComplete = (e.loaded / e.total) * 100;
            onProgress(percentComplete, e.loaded, e.total);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve({
              url: publicUrl,
              publicId: key,
              originalName: file.name,
              fileSize: file.size,
              fileType: file.type
            });
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };


  const handleDigitalAssetAdd = async (files) => {
    if (!files?.length) return;

    // Only take the first file
    const fileToProcess = [files[0]];

    // Check if we already have a file
    if (pendingDigitalAssets.length >= 1) {
      setErrorMessage('You can only upload 1 file. Remove the existing file first.');
      return;
    }

    const countCheck = checkDigitalFileCount(pendingDigitalAssets.length, fileToProcess.length);
    if (!countCheck.canAdd) {
      setErrorMessage(countCheck.message);
      return;
    }

    setUploadingAssets(true);
    setErrorMessage('');

    try {
      const newAssets = [];
      let accumulatedSize = pendingDigitalAssets.reduce((sum, a) => sum + a.fileSize, 0);

      for (const file of files) {
        const sizeCheck = checkDigitalFileSize(file.size);
        if (!sizeCheck.canAdd) {
          setErrorMessage(`${file.name}: ${sizeCheck.message}`);
          continue;
        }

        // Check total size limit with accumulated size
        const totalCheck = checkTotalDigitalSize(
          accumulatedSize,
          file.size,
          pendingDigitalAssets.map(a => a.fileSize)
        );
        if (!totalCheck.canAdd) {
          setErrorMessage(totalCheck.message);
          break;
        }

        let asset;

        try {
          if (file.size <= 5 * 1024 * 1024) {
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result.split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            asset = {
              id: Date.now() + Math.random(),
              filename: file.name,
              originalName: file.name,
              fileSize: file.size,
              fileType: file.type,
              base64Data: base64,
              isTemp: true,
              uploadMethod: 'base64'
            };
          } else {
            console.log('Uploading large file directly:', file.name);
            const uploadResult = await uploadLargeFile(file);

            asset = {
              id: Date.now() + Math.random(),
              filename: uploadResult.publicId,
              originalName: uploadResult.originalName,
              fileSize: uploadResult.fileSize,
              fileType: uploadResult.fileType,
              storageUrl: uploadResult.url,
              isTemp: true,
              uploadMethod: 'direct'
            };
          }

          newAssets.push(asset);
          accumulatedSize += file.size;
        } catch (fileError) {
          console.error('Error processing file:', file.name, fileError);
          setErrorMessage(`Failed to upload ${file.name}: ${fileError.message}`);
        }
      }

      if (newAssets.length > 0) {
        setPendingDigitalAssets(prev => [...prev, ...newAssets]);
      }
    } catch (error) {
      console.error('Error preparing digital assets:', error);
      setErrorMessage(error.message);
    } finally {
      setUploadingAssets(false);
    }
  };



  const handleRemovePendingAsset = async (assetId) => {
    const assetToRemove = pendingDigitalAssets.find(a => a.id === assetId);

    // Cleanup uploaded file if it was direct upload
    if (assetToRemove?.uploadMethod === 'direct' && assetToRemove?.filename) {
      await cleanupTempFiles([assetToRemove.filename]);
    }

    setPendingDigitalAssets(prev => prev.filter(asset => asset.id !== assetId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDigitalAssetUploaded = () => {
    // Refresh data and close modal after digital asset upload
    resetForm();
    onClose();
  };

  const handleSkipDigitalUpload = () => {
    // Allow skipping digital upload for now
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setPrice('');
    setCategory('');
    setNewCategory('');
    setImage(null);
    setShowAdvanced(false);
    setScheduledStart('');
    setScheduledEnd('');
    setRepeatType('');
    setRepeatTime('');
    setRepeatDay('');
    setRepeatDate('');
    setPreviewUrl('');
    setFormattedPrice('');
    // Reset inventory fields
    setManageInventory(false);
    setInitialStock(0);
    setSku('');
    setLowStockThreshold(5);
    setShowInventory(false);
    // Reset digital product fields
    setProductType('PHYSICAL');
    setShowDigitalUpload(false);
    setStep('CREATE_ITEM');
    setCreatedItem(null);
    // Reset pending digital assets
    setPendingDigitalAssets([]);
    setUploadingAssets(false);

    // Reset digital settings
    setDigitalSettings({
      downloadLimit: 5,
      linkExpiryHours: 48,
      allowPreview: false,
      previewDuration: 30
    });
    setPendingDigitalLinks([]);
  };


  const handleImageChange = async (e) => {

    setIsCompressing(true);
    setErrorMessage('');

    const file = e.target.files[0];
    if (file) {
      try {
        // Check file size (e.g., max 100MB)
        if (file.size > 100 * 1024 * 1024) {
          throw new Error('File size exceeds 100MB. Please choose a smaller image.');
        }

        let processedFile = file;

        // Check if the file is HEIC
        if (file.type === 'image/heic' || file.type === 'image/heif') {
          try {
            const jpegBlob = await heic2any({
              blob: file,
              toType: 'image/jpeg',
              quality: 0.8
            });
            processedFile = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
              type: 'image/jpeg'
            });
          } catch (heicError) {
            throw new Error('Unable to convert HEIC image. Please try a different format.');
          }
        }

        // Check if the file is a valid image type
        if (!processedFile.type.startsWith('image/')) {
          throw new Error('Invalid file type. Please upload an image file.');
        }

        const options = {
          maxSizeMB: 4,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(processedFile, options);

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          setImage(base64String);
          setPreviewUrl(reader.result);
          setIsCompressing(false);
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          throw new Error('Error reading file. Please try again.');

        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error processing image:', error);
        setErrorMessage(error.message || 'An error occurred while processing the image. Please try again.');
        setIsCompressing(false);
        setImage(null);
        setPreviewUrl('');
      }
    }
  };

  if (!isOpen) return null;

  const currentFileCount = pendingDigitalAssets.length + pendingDigitalLinks.length;
  const currentTotalSize = pendingDigitalAssets.reduce((sum, a) => sum + a.fileSize, 0);
  const maxFiles = limits?.digitalFiles?.maxFilesPerProduct || 5;
  const maxFileSize = limits?.digitalFiles?.maxFileSize || 100 * 1024 * 1024;
  const maxTotalSize = limits?.digitalFiles?.maxTotalSizePerProduct || 500 * 1024 * 1024;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-xl font-bold text-[#191970]">
            Add New Item
          </h4>
          <button onClick={handleCancel} className="text-gray-500 bg-gray-100 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>


        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-500">Name *</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-500">Description <span className='text-[70%]'>(optional)</span></label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-20"
              rows="3"
            ></textarea>
          </div>

          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-500">Price *</label>
            <div className='flex flex-row'>
              <p className='mt-4 mr-2'>₦</p>
              <input
                type="text"
                id="price"
                value={formattedPrice}
                onChange={handlePriceChange}
                required
                className="mt-1 px-2 py-2 block w-full rounded-md bg-white border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
              />
            </div>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-500">Category <span className='text-[70%]'>(optional)</span></label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 px-2 py-2 block w-full rounded-md bg-white border-[1.5px] border-gray-200 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50"
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Product Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Type
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { value: 'PHYSICAL', label: 'Physical', icon: '📦' },
                { value: 'DIGITAL', label: 'Digital', icon: '💾' },
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => {
                    setProductType(type.value);
                    setShowDigitalUpload(type.value !== 'PHYSICAL');
                  }}
                  className={`p-3 bg-white rounded-lg border-2 transition-all duration-200 ${productType === type.value
                    ? 'border-[#191970] bg-[#191970]/5 text-[#191970] '
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                >
                  <div className="text-center">
                    <div className="text-lg mb-1">{type.icon}</div>
                    <div className="text-xs sm:text-sm font-medium">{type.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Digital Product Section */}
          {showDigitalUpload && (
            <div className="bg-blue-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between"> {/* CHANGE from space-x-2 to justify-between */}
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xs">💾</span>
                  </div>
                  <h3 className="text-sm font-medium text-blue-900">Digital Content</h3>
                </div>
                {/* ADD: Show usage indicator */}
                <span className="text-xs text-gray-600">
                  {currentFileCount}/{maxFiles} files
                </span>
              </div>

              {/* Asset Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What type of digital content?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDigitalAssetType('files');
                      setPendingDigitalLinks([]);
                    }}
                    className={`p-3 bg-transparent rounded-lg border-2 transition-all ${digitalAssetType === 'files'
                      ? 'border-[#191970] bg-[#191970]/5 text-[#191970]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                  >
                    <div className="text-center">
                      <div className="text-lg mb-1">📁</div>
                      <div className="text-xs font-medium">Upload Files</div>
                      <div className="text-xs text-gray-500 mt-1">PDF, ZIP, etc.</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setDigitalAssetType('links');
                      setPendingDigitalAssets([]);
                    }}
                    className={`p-3 rounded-lg bg-transparent border-2 transition-all ${digitalAssetType === 'links'
                      ? 'border-[#191970] bg-[#191970]/5 text-[#191970]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                  >
                    <div className="text-center">
                      <div className="text-lg mb-1">🔗</div>
                      <div className="text-xs font-medium">Add Links</div>
                      <div className="text-xs text-gray-500 mt-1">WhatsApp, Drive, etc.</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* File Upload Section */}
              {digitalAssetType === 'files' && (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      id="digital-files"
                      // multiple
                      onChange={(e) => handleDigitalAssetAdd(Array.from(e.target.files))}
                      accept=".pdf,.zip,.mp3,.mp4,.doc,.docx,.ppt,.pptx,.txt,.jpg,.png"
                      disabled={uploadingAssets || currentFileCount >= maxFiles}
                      className="sr-only"
                    />
                    <label
                      htmlFor="digital-files"
                      className={`cursor-pointer block ${(uploadingAssets || currentFileCount >= maxFiles) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <Plus className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {uploadingAssets ? 'Processing...' :
                          currentFileCount >= maxFiles ? 'File limit reached' :
                            'Choose files to upload'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Max {maxFileSize / (1024 * 1024)}MB per file{maxFiles > 1 ? `, ${maxFiles} files total` : ''}
                      </p>
                      {/* <p className="text-xs text-gray-500 mt-1">
                        {formatFileSize(currentTotalSize)} / {formatFileSize(maxTotalSize)} used
                      </p> */}
                    </label>
                  </div>

                  {pendingDigitalAssets.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Files ready to upload:</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {pendingDigitalAssets.map((asset) => (
                          <div key={asset.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                            <div className="flex items-center space-x-2 min-w-0">
                              <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                <Package className="w-3 h-3 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{asset.originalName}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(asset.fileSize)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemovePendingAsset(asset.id)}
                              className="flex-shrink-0 bg-gray-50 p-1 text-red-500 hover:text-red-700 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Links Section */}
              {digitalAssetType === 'links' && (
                <div className="space-y-3">
                  {pendingDigitalLinks.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Links to add:</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {pendingDigitalLinks.map((link) => (
                          <div key={link.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                            <div className="flex items-center space-x-2 min-w-0">
                              <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                <span className="text-xs">🔗</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {link.linkDescription || link.originalName}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {link.linkType.replace('_', ' ')} • {link.linkUrl.substring(0, 25)}...
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPendingDigitalLinks(prev => prev.filter(l => l.id !== link.id))}
                              className="flex-shrink-0 p-1 text-red-500 hover:text-red-700 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <DigitalLinkForm
                    onLinkAdd={(newLink) => {
                      setPendingDigitalLinks(prev => [...prev, {
                        ...newLink,
                        assetType: 'URL',
                        id: Date.now() + Math.random()
                      }]);
                    }}
                  />
                </div>
              )}

              {/* Digital Settings */}
              {(pendingDigitalAssets.length > 0 || pendingDigitalLinks.length > 0) && (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <Settings className="h-4 w-4 text-gray-600" />
                    <h4 className="text-sm font-medium text-gray-700">Access Settings</h4>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Usage Limit
                      </label>
                      <select
                        value={digitalSettings.downloadLimit}
                        onChange={(e) => handleDigitalSettingsChange('downloadLimit', parseInt(e.target.value))}
                        className="w-full bg-white px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#191970] focus:ring-1 focus:ring-[#191970]"
                      >
                        <option value={-1}>Unlimited</option>
                        <option value={1}>1 download</option>
                        <option value={3}>3 downloads</option>
                        <option value={5}>5 downloads</option>
                        <option value={10}>10 downloads</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Link Expiry
                      </label>
                      <select
                        value={digitalSettings.linkExpiryHours}
                        onChange={(e) => handleDigitalSettingsChange('linkExpiryHours', parseInt(e.target.value))}
                        className="w-full bg-white px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#191970] focus:ring-1 focus:ring-[#191970]"
                      >
                        <option value={-1}>Never expires</option>
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={168}>1 week</option>
                        <option value={720}>1 month</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}




          {/* Product Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
            <div className="space-y-3">
              <input
                type="file"
                id="image"
                onChange={handleImageChange}
                accept="image/*"
                disabled={isCompressing}
                className="sr-only"
              />

              {previewUrl ? (
                <div className="relative inline-block">
                  <img src={previewUrl} alt="Preview" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
                  <button
                    type="button"
                    onClick={() => {
                      setImage(null);
                      setPreviewUrl('');
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="image"
                  className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-2 pb-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                      <span className="text-gray-400 text-sm">📷</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {isCompressing ? 'Processing...' : 'Add product image'}
                    </p>
                  </div>
                </label>
              )}

              {errorMessage && (
                <p className="text-xs text-red-600">{errorMessage}</p>
              )}
            </div>
          </div>

          {/* Inventory Management Section */}
          {/* Inventory Management Section - Updated */}
          {productType === 'PHYSICAL' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowInventory(!showInventory)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Package size={16} className="text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-gray-900">Inventory Management</h3>
                    <p className="text-xs text-gray-500">Track stock levels and get low stock alerts</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {manageInventory && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Enabled
                    </span>
                  )}
                  {showInventory ? (
                    <ChevronUp size={16} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={16} className="text-gray-400" />
                  )}
                </div>
              </button>

              {showInventory && (
                <div className="p-4 border-t border-gray-200 space-y-4">
                  {/* Toggle Switch */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Enable Inventory Tracking</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Monitor stock levels and receive alerts when running low
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setManageInventory(!manageInventory)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#191970] focus:ring-offset-2 ${manageInventory ? 'bg-[#191970]' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${manageInventory ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Inventory Fields */}
                  <div className={`space-y-4 transition-all duration-200 ${manageInventory ? 'opacity-100' : 'opacity-50 pointer-events-none'
                    }`}>
                    {/* Stock Information Card */}
                    <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                          <span className="text-blue-600 text-xs">📦</span>
                        </div>
                        <h4 className="text-sm font-medium text-blue-900">Stock Information</h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="initialStock" className="block text-sm font-medium text-gray-700 mb-2">
                            Initial Stock Quantity
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              id="initialStock"
                              min="0"
                              value={initialStock}
                              onChange={(e) => setInitialStock(parseInt(e.target.value) || 0)}
                              disabled={!manageInventory}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white shadow-sm focus:border-[#191970] focus:ring-1 focus:ring-[#191970] transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                              placeholder="0"
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <span className="text-xs text-gray-400">units</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            How many units do you currently have?
                          </p>
                        </div>

                        <div>
                          <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700 mb-2">
                            Low Stock Alert
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              id="lowStockThreshold"
                              min="1"
                              value={lowStockThreshold}
                              onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 5)}
                              disabled={!manageInventory}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white shadow-sm focus:border-[#191970] focus:ring-1 focus:ring-[#191970] transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                              placeholder="5"
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <span className="text-xs text-gray-400">units</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Get notified when stock falls below this level
                          </p>
                        </div>
                      </div>

                      {/* Stock Preview */}
                      {manageInventory && initialStock > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${initialStock > lowStockThreshold
                                ? 'bg-green-400'
                                : initialStock > 0
                                  ? 'bg-yellow-400'
                                  : 'bg-red-400'
                                }`}></div>
                              <span className="text-sm font-medium text-gray-700">
                                Stock Status: {
                                  initialStock > lowStockThreshold
                                    ? 'Good'
                                    : initialStock > 0
                                      ? 'Low Stock'
                                      : 'Out of Stock'
                                }
                              </span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {initialStock} units available
                            </span>
                          </div>
                          {initialStock <= lowStockThreshold && initialStock > 0 && (
                            <p className="text-xs text-yellow-600 mt-1">
                              ⚠️ This is below your low stock threshold
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* SKU Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-5 h-5 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-gray-600 text-xs">#</span>
                        </div>
                        <h4 className="text-sm font-medium text-gray-700">Product Identification</h4>
                      </div>

                      <div>
                        <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-2">
                          SKU (Stock Keeping Unit)
                          <span className="text-xs text-gray-500 font-normal ml-1">(optional)</span>
                        </label>
                        <input
                          type="text"
                          id="sku"
                          value={sku}
                          onChange={(e) => setSku(e.target.value)}
                          disabled={!manageInventory}
                          placeholder="e.g., PROD-001, SHIRT-BLU-L"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white shadow-sm focus:border-[#191970] focus:ring-1 focus:ring-[#191970] transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Unique identifier for tracking and inventory management
                        </p>
                      </div>
                    </div>

                    {/* Benefits Info */}
                    {manageInventory && (
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <div className="flex items-start space-x-2">
                          <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                            <span className="text-green-600 text-xs">✓</span>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-green-900 mb-1">
                              Inventory tracking enabled
                            </h5>
                            <ul className="text-xs text-green-700 space-y-1">
                              <li>• Automatic stock updates when orders are placed</li>
                              <li>• Low stock alerts to prevent overselling</li>
                              <li>• Better inventory management and reporting</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}



          {/* Submit Button */}
          <div className="pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isAdding}
              className="w-full py-3 px-4 bg-[#191970] text-white text-sm font-medium rounded-lg hover:bg-[#191970]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#191970] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isAdding ? 'Adding item...' : 'Add Item'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default AddItemPopup;
